import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { isFeatureEnabledForUser } from 'components/Optimizely/Optimizely.sdk';

import { reportError } from 'helpers/error-logging';

import { selectUserId } from 'selectors/general';

// The Optimizely datafile comes from a third-party CDN through an axios client
// with no request timeout, so a stalled response never settles. Callers that
// wait on a flag before rendering or fetching would hang indefinitely.
export const FLAG_RESOLUTION_TIMEOUT_MS = 2000;

export function useIsFeatureEnabled(flagKey, attributes) {
  const userId = useSelector(selectUserId);
  const [isEnabled, setIsEnabled] = useState(null);
  // Callers pass a fresh object literal on every render, so the effect depends on the
  // serialized form instead to avoid re-resolving the flag in a loop.
  const serializedAttributes = JSON.stringify(attributes || {});

  useEffect(() => {
    let isMounted = true;

    const timeoutId = setTimeout(() => {
      if (isMounted) setIsEnabled(current => (current === null ? false : current));
    }, FLAG_RESOLUTION_TIMEOUT_MS);

    async function loadFlag() {
      try {
        const enabled = await isFeatureEnabledForUser(flagKey, userId, JSON.parse(serializedAttributes));

        if (isMounted) setIsEnabled(enabled);
      } catch (error) {
        // Fail closed: on a flag-resolution error keep the safe (off) default.
        reportError('Failed to resolve feature flag', { error, flagKey, userId });
        if (isMounted) setIsEnabled(false);
      }
    }

    if (userId && flagKey) {
      loadFlag();
    } else {
      setIsEnabled(false);
    }

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [flagKey, serializedAttributes, userId]);

  return isEnabled;
}
